import React, { useEffect, useRef, useState } from 'react';

import {
  getAuth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  PhoneAuthProvider,
  signInWithCredential,
} from 'firebase/auth';
import { Button, Icon, Modal, toast, Tooltip, Typography } from 'pixel-react';

import { useVerifyOtpMutation } from '@/store/userProfile/userProfileSlice';

import { firebaseApp, firebaseConfig } from './firebase';
import styles from './OtpVerification.module.scss';
import type { OtpVerificationProps } from './type';

const OtpVerification: React.FC<OtpVerificationProps> = ({
  phoneNumber,
  setPhoneVerified,
  setIsOtpModalOpen,
  setIsDisabled,
}) => {
  const [otpValue, setOtpValue] = useState<string[]>(new Array(6).fill(''));
  const [error, setError] = useState<string>('');
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const recaptchaVerifierRef = useRef<any>(null);
  const [verifyOtpMutation] = useVerifyOtpMutation();

  const sendOtpToPhoneNumber = async () => {
    if (recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current.clear();
      recaptchaVerifierRef.current = null;
    }
    const auth = getAuth(firebaseApp);
    recaptchaVerifierRef.current = new RecaptchaVerifier(
      auth,
      'sign-in-button',
      {
        size: 'invisible',
      },
    );

    await recaptchaVerifierRef.current.verify();

    const formattedPhoneNumber = phoneNumber.startsWith('+')
      ? phoneNumber
      : `+${phoneNumber}`;

    signInWithPhoneNumber(
      auth,
      formattedPhoneNumber,
      recaptchaVerifierRef.current,
    )
      .then((confirmationResult: any) => {
        toast.success('OTP has been sent successfully');
        setVerificationId(confirmationResult.verificationId);
        // ...
      })
      .catch((error: any) => {
        // Error; SMS not sent
        console.error('Error during OTP sending:', error);
        setVerificationId(null);
        setPhoneVerified(false);
      });
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const value = e.target.value.replace(/\D/g, '');
    const updatedOtp = [...otpValue];
    updatedOtp[index] = value;
    setOtpValue(updatedOtp);

    if (value && index < otpValue.length - 1) {
      const nextInput = document.getElementById(
        `otp-input-${index + 1}`,
      ) as HTMLInputElement;
      nextInput?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === 'Backspace' && !otpValue[index]) {
      const prevInput = document.getElementById(
        `otp-input-${index - 1}`,
      ) as HTMLInputElement;
      prevInput?.focus();
    }
  };

  const verifyOtp = async () => {
    const otp = otpValue.join('');
    if (otp.length === 6 && verificationId) {
      try {
        await verifyOtpMutation({
          payload: { otp, sessionInfo: verificationId },
          key: firebaseConfig.apiKey,
        }).unwrap();
        // Pass the varification to java api
        // const credential = PhoneAuthProvider.credential(verificationId, otp);
        // await signInWithCredential(getAuth(firebaseApp), credential);
        setError('');
        setPhoneVerified(true);
        setIsOtpModalOpen(false);
        setIsDisabled(false);
        toast.success('OTP Verified successfully');
      } catch (error: any) {
        setPhoneVerified(false);
        console.error('Error during OTP verification:', error);
        setError('Please enter valid OTP');
      }
    } else if (otp.length === 6 && !verificationId) {
      setPhoneVerified(false);
      setError('Please enter valid OTP');
    } else {
      setError('Verification Code is required');
    }
  };

  useEffect(() => {
    sendOtpToPhoneNumber();
    return () => {
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      }
    };
  }, []);
  const resendOtp = async () => {
    const auth = getAuth(firebaseApp);
    const formattedPhoneNumber = phoneNumber.startsWith('+')
      ? phoneNumber
      : `+${phoneNumber}`;

    signInWithPhoneNumber(
      auth,
      formattedPhoneNumber,
      recaptchaVerifierRef.current!,
    )
      .then((confirmationResult: any) => {
        toast.success('OTP has been sent successfully');
        setVerificationId(confirmationResult.verificationId);
        // ...
      })
      .catch((error: any) => {
        // Error; SMS not sent
        console.error('Error during OTP sending:', error);
        setVerificationId(null);
        setPhoneVerified(false);
      });
  };
  const handleClose = () => {
    setIsOtpModalOpen(false);
  };

  return (
    <Modal
      isOpen
      onClose={() => {}}
      isFooterDisplayed={false}
      isHeaderDisplayed={false}
      customWidth="312px"
      zIndex={999999}
    >
      <div className={styles.container}>
        <span className={styles.close_icon}>
          <Tooltip title="Close">
            <Icon name="close" height={16} width={16} onClick={handleClose} />
          </Tooltip>
        </span>
        <Typography fontSize={24} lineHeight="36px" fontWeight="semi-bold">
          OTP Verification
        </Typography>
        <div className={styles.input_container}>
          {otpValue.map((value, index) => (
            <input
              key={index}
              id={`otp-input-${index}`}
              type="text"
              value={value}
              onChange={e => handleInputChange(e, index)}
              onKeyDown={e => handleKeyDown(e, index)}
              maxLength={1}
              className={styles.otp_input}
              autoFocus={index === 0}
              autoComplete="off"
            />
          ))}
        </div>
        {error && (
          <Typography
            className={styles.error}
            color="var(--input-error-text-color)"
          >
            {error}
          </Typography>
        )}
        <div>
          <div id="sign-in-button" style={{ display: 'none' }} />
          <Button variant="primary" label="Submit" onClick={verifyOtp} />
          <Typography
            color="var(--brand-color)"
            fontSize={10}
            lineHeight="15px"
            className={styles.resent_otp}
            onClick={resendOtp}
          >
            Re-send OTP
          </Typography>
        </div>
      </div>
    </Modal>
  );
};

export default OtpVerification;
